import { Column, DataTable, useColumns } from "@/components/data-table";
import { useFilters } from "@/components/filters";
import { ListPage } from "@/components/list-page";
import { useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { getAllCajaActualAperturaActualMovimientos, useGetAllCajaActualAperturaActualMovimientosQuery } from "@/features/cajas/caja-actual/api/get-all-caja-actual-apertura-actual-movimientos";
import { useCajaContext } from "@/features/cajas/caja-actual/contexts/caja-context";
import useGetMonedasOfTransferencia from "@/features/cajas/shared/hooks/use-get-monedas-of-transferencia";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { getTotalMonedaTransferencia } from "@/features/monetizacion/shared/utils";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { CajaMovimiento } from "@/shared/types/caja-movimiento";
import { Permiso } from "@/shared/types/permiso";
import { Button, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import ViewCajaActualMovimientoModal from "./view-caja-actual-movimiento-modal";
import AddCajaMovimientoModal from "./add-caja-movimiento-modal";

export default function CajaOpened() {
    const { monedaBase } = useMonetizacion();
    const hasClosePermiso = useHasPermisos(Permiso.AperturaCierreCajas);
    const hasWriteMovimientoPermiso = useHasPermisos(Permiso.WriteMovimientoCajas);
    const hasViewHistorialPermiso = useHasPermisos(Permiso.HistorialAperturaCajaActual);
    const openModal = useOpenModal();
    const { isCajaOpen } = useCajaContext();

    const filterData = useFilters({
        filters: [
            { type: "dateRange", fromName: "desde", toName: "hasta", label: "Fecha" },
            { type: "pagination" }
        ],
        useURL: true
    })

    const fetchData = useGetAllCajaActualAperturaActualMovimientosQuery(filterData.filterData, { enabled: isCajaOpen });

    const monedas = useGetMonedasOfTransferencia(fetchData.data?.result.map(cm => cm.transferencia) || [], true);

    const columnData = useColumns<CajaMovimiento>({
        key: "caja-actual",
        columns: [
            { key: "fecha", header: "Fecha", render: row => formatDate(row.fecha, true), width: "auto" },
            { key: "motivo", header: "Motivo", render: row => formatString(row.descripcion) },
            ...monedas.map<Column>(m => ({
                key: `${m.nombre}SubTotal${m.hidden ? "Hidden" : ""}`,
                header: <Typography variant="inherit" color={m.hidden ? "textDisabled" : "textPrimary"}>
                    Subtotal ({m.simbolo})
                </Typography>,
                render: (row: CajaMovimiento) => <Typography variant="inherit" color={m.hidden ? "textDisabled" : "textPrimary"}>
                    {formatNumber(getTotalMonedaTransferencia(row.transferencia, m.id))}
                </Typography>,
                width: "auto",
                align: "right",
                show: !m.hidden
            })),
            { key: "total", header: `Total Aprox. (${monedaBase.simbolo})`, render: row => formatNumber(row.total), align: "right", width: "auto" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ]
    })

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Caja Actual" >
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />

                {(hasViewHistorialPermiso || hasWriteMovimientoPermiso || hasClosePermiso) && <ListPage.Header.Actions.Divider />}
                {hasViewHistorialPermiso && <Button variant="outlined" size="small" onClick={() => openModal("viewHistorialCajaActual")}>Historial de Arqueo</Button>}
                {hasWriteMovimientoPermiso && <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => openModal("addCajaMovimiento")}>Agregar Movimiento</Button>}
                {hasClosePermiso && <Button variant="contained" size="small" onClick={() => openModal("closeCaja")}>Cerrar Caja</Button>}

                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                loading={fetchData.isFetching}
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                emptyText="La caja esta abierta."
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <ViewCajaActualMovimientoModal code="view" />

        <AddCajaMovimientoModal code="addCajaMovimiento" />

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajaActualAperturaActualMovimientos}
            defaultFileTitle="Movimientos de Caja"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajaActualAperturaActualMovimientos}
            defaultFileTitle="Movimientos de Caja"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajaActualAperturaActualMovimientos}
            defaultFileTitle="Movimientos de Caja"
        />
    </ListPage>
}